import React, { useEffect, useCallback } from 'react';

import base64 from 'base-64';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import BarcodeIcon from 'apps/web_forms/assets/barcode-icon.svg';
import ErrorMessage from 'apps/web_forms/components/commons/ErrorMessage';
import ImageUploadElement from 'apps/web_forms/components/commons/ImageUploadElement';
import useChangeValue from 'apps/web_forms/hooks/useChangeValue';
import useResourceUpdateHighlight from 'apps/web_forms/hooks/useResourceUpdateHighlight';
import { selectFieldErrorById } from 'apps/web_forms/hooks/useGlobalSelector';
import useRules from 'apps/web_forms/hooks/useRules';
import { setCurrentSelectedMultimediaInput, addBarcodeValue } from 'apps/web_forms/slices/multimediaSlice';
import customPropTypes from 'apps/web_forms/utils/customPropTypes';

const BarcodeInput = ({
  element,
  formContext,
  onChangeValue,
  toggleActiveValue,
  isDriverLicense,
  showContent,
}) => {
  const {
    title,
    identifier,
    hint,
    hidden,
    validate_msg: validateMsg,
    allow_manual_override: allowManualOverride,
  } = element;
  const { register } = formContext;

  const { changeValue, currentValue } = useChangeValue(
    element,
    onChangeValue,
    formContext
  );
  
  const errorMessage = useSelector((state) => selectFieldErrorById(state, identifier));
  const barcodeFromCamera = useSelector((state) => state.multimedia.barcodeValues[identifier]);

  const { hasResourceUpdateHighlight, removeResourceUpdateHighlight } =
    useResourceUpdateHighlight({ fieldIdentifier: identifier });

  const dispatch = useDispatch();

  const [{ isActive, isRequired, isElementValid, isReadOnly, initialAnswer }] =
    useRules(element, formContext);

  useEffect(() => {
    changeValue(initialAnswer);
    if(initialAnswer !== undefined){
      dispatch(
        addBarcodeValue({
          key: identifier,
          value: initialAnswer,
        })
      );
    }
  }, [initialAnswer]);

  const sendValue = (text) => {
    const newValue = isDriverLicense && text !== undefined && text !== ''
      ? base64.encode(text)
      : text;
    changeValue(newValue, true);
    dispatch(setCurrentSelectedMultimediaInput({}));
  };

  useEffect(() => {
    if (barcodeFromCamera) {
      sendValue(barcodeFromCamera);
    }
  }, [barcodeFromCamera]);

  useEffect(() => {
    register(identifier,{
      validate: () => isElementValid || validateMsg || false ,
      required: isRequired ? 'This field is required' : false,
    });
  }, [identifier, isElementValid, validateMsg, isRequired]);

  const handleCameraButtonClick = useCallback(() => {
    if (!isReadOnly) {
      dispatch(setCurrentSelectedMultimediaInput({ element }));
    }
  }, [isReadOnly]);

  const handleNewImage = (image) => {
    dispatch(
      setCurrentSelectedMultimediaInput({
        element,
        additionalProps: { uploadedImageSrc: image, isDriverLicense },
      })
    );
  };

  useEffect(() => {
    toggleActiveValue(identifier, isActive);
  }, [isActive]);

  if (!isActive || hidden || !showContent) return null;

  return (
    <div className='react-form-control'>
      <label htmlFor={identifier}>{title}</label>
      <ImageUploadElement
        id={identifier}
        hint={hint}
        cameraButtonIcon={BarcodeIcon}
        isRequired={isRequired}
        isInvalid={errorMessage !== undefined}
        hasResourceUpdateHighlight={hasResourceUpdateHighlight}
        removeResourceUpdateHighlight={removeResourceUpdateHighlight}
        isReadOnly={isReadOnly}
        textValue={currentValue}
        allowTextOverride={allowManualOverride}
        onChangeText={changeValue}
        onCameraButtonClick={handleCameraButtonClick}
        onNewImage={handleNewImage}
        toggleActiveValue={toggleActiveValue}
        formContext={formContext}
        noThumbnail
      />
      {errorMessage !== undefined && (
        <ErrorMessage id={`${identifier}_errmsg`}>{errorMessage}</ErrorMessage>
      )}
    </div>
  );
};

BarcodeInput.propTypes = {
  element: PropTypes.shape({
    title: PropTypes.string,
    identifier: PropTypes.string,
    hint: PropTypes.string,
    hidden: PropTypes.bool,
    validate_msg: PropTypes.string,
    barcode_type: PropTypes.string,
    allow_manual_override: PropTypes.bool,
  }).isRequired,
  formContext: customPropTypes.formContext.isRequired,
  onChangeValue: PropTypes.func.isRequired,
  isDriverLicense: PropTypes.bool,
};

BarcodeInput.defaultProps = {
  isDriverLicense: false,
};

export default React.memo(BarcodeInput);
